'use client'

import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import MUIStack from '@mui/material/Stack'
import MUIButton from '@mui/material/Button'
import { TextEntry } from '@repo/ui/TextEntry'
import { AddressSelect } from '@repo/ui/AddressSelect'

// Helper to strip newline characters so the string stays on a single line
const sanitizeSingleLine = (str) => str.replace(/[\r\n]/g, '')

export const AddressInput = ({ defaultValue, onChange, validationErrors, sx = {}, searchWidth, fieldWidth, hideExtraDiv, fieldSize = 'medium' }) => {
  const [house, setHouse] = useState(sanitizeSingleLine(defaultValue?.house || ''))
  const [area, setArea] = useState(sanitizeSingleLine(defaultValue?.area || ''))
  const [postalTown, setPostalTown] = useState(sanitizeSingleLine(defaultValue?.postalTown || ''))
  const [county, setCounty] = useState(sanitizeSingleLine(defaultValue?.county || ''))
  const [postcode, setPostcode] = useState(sanitizeSingleLine(defaultValue?.postcode || ''))
  const [companyName, setCompanyName] = useState(sanitizeSingleLine(defaultValue?.companyName || ''))
  const [addressSearchTerm, setAddressSearchTerm] = useState('')

  const [manualAddressMode, setManualAddressMode] = useState(!!postcode)

  const toggleManualAddressMode = () => {
    setManualAddressMode(!manualAddressMode)
  }

  const handlePostcodeKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      setAddressSearchTerm(sanitizeSingleLine(postcode))
    }
  }

  const handleAddressSelection = ({ house: h, area: a, postalTown: pT, county: c, postcode: pC, companyName: cN }) => {
    const sanitizedHouse = sanitizeSingleLine(h || '')
    const sanitizedArea = sanitizeSingleLine(a || '')
    const sanitizedPostalTown = sanitizeSingleLine(pT || '')
    const sanitizedCounty = sanitizeSingleLine(c || '')
    const sanitizedPostcode = sanitizeSingleLine(pC || '')
    const sanitizedCompanyName = sanitizeSingleLine(cN || '')

    setHouse(sanitizedHouse)
    setArea(sanitizedArea)
    setPostalTown(sanitizedPostalTown)
    setCounty(sanitizedCounty)
    setPostcode(sanitizedPostcode)
    setCompanyName(sanitizedCompanyName)
    setManualAddressMode(true)
  }

  useEffect(() => {
    if (!onChange) return
    onChange({ house, area, postalTown, county, postcode, companyName })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [house, area, postalTown, county, postcode, companyName])

  const currentFieldWidth = fieldWidth || 'calc(50% - 8px)'
  const currentSearchWidth = searchWidth || 'calc(50% - 8px)'

  return (
    <>
      <MUIStack direction="row" gap="16px" data-testid="AddressInput" flexWrap="wrap" sx={{ ...sx }}>
        {manualAddressMode ? (
          <>
            <TextEntry
              size={fieldSize}
              name="house"
              label="House"
              limit={50}
              value={house}
              onChange={(v) => setHouse(sanitizeSingleLine(v))}
              errors={validationErrors}
              sx={{ width: currentFieldWidth, backgroundColor: 'background.paper' }}
            />
            <TextEntry
              size={fieldSize}
              name="area"
              label="Area"
              limit={50}
              value={area}
              onChange={(v) => setArea(sanitizeSingleLine(v))}
              errors={validationErrors}
              sx={{ width: currentFieldWidth, backgroundColor: 'background.paper' }}
            />
            <TextEntry
              size={fieldSize}
              name="postalTown"
              label="Town"
              limit={50}
              value={postalTown}
              onChange={(v) => setPostalTown(sanitizeSingleLine(v))}
              errors={validationErrors}
              sx={{ width: currentFieldWidth, backgroundColor: 'background.paper' }}
            />
            <TextEntry
              size={fieldSize}
              name="county"
              label="County"
              limit={50}
              value={county}
              onChange={(v) => setCounty(sanitizeSingleLine(v))}
              errors={validationErrors}
              sx={{ width: currentFieldWidth, backgroundColor: 'background.paper' }}
            />
          </>
        ) : null}

        <MUIStack direction="row" gap="16px" sx={{ width: '100%', maxWidth: currentSearchWidth }}>
          <TextEntry
            name="postcode"
            size={fieldSize}
            label="Postcode"
            limit={10}
            value={postcode}
            onChange={(v) => setPostcode(sanitizeSingleLine(v))}
            onKeyDown={handlePostcodeKeyPress}
            errors={validationErrors}
            sx={{ backgroundColor: 'background.paper' }}
          />

          {!manualAddressMode ? (
            <MUIButton
              variant="contained"
              sx={{ height: fieldSize === 'medium' ? '53px' : '40px' }}
              disabled={!postcode}
              onClick={() => {
                // set it to empty string to trigger a new search
                if (addressSearchTerm) setAddressSearchTerm('')
                setTimeout(() => {
                  setAddressSearchTerm(sanitizeSingleLine(postcode))
                }, 1)
              }}
            >
              Find
            </MUIButton>
          ) : null}
        </MUIStack>

        <div style={{ width: '50px', display: hideExtraDiv ? 'none' : 'block' }} />
        <AddressSelect
          onChange={handleAddressSelection}
          size={fieldSize}
          searchTerm={addressSearchTerm}
          visible={!manualAddressMode && addressSearchTerm === postcode}
          sx={{ width: '100%', height: '53px' }}
        />
      </MUIStack>

      <TextLinkButton
        title={manualAddressMode ? 'Search by postcode' : 'Enter address manually'}
        onClick={toggleManualAddressMode}
        sx={{ mt: -5, width: manualAddressMode ? '132px' : '160px' }}
      />
    </>
  )
}

AddressInput.propTypes = {
  defaultValue: PropTypes.object,
  validationErrors: PropTypes.object,
  onChange: PropTypes.func,
  sx: PropTypes.object,
  searchWidth: PropTypes.string,
  fieldWidth: PropTypes.string,
  hideExtraDiv: PropTypes.bool,
  fieldSize: PropTypes.string
}
